import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, CheckCircle2, XCircle, CreditCard } from "lucide-react";
import { Button } from "../components/ui/button";
import { api, errMsg, money } from "../lib/api";
import { useAuth, homeFor } from "../context/AuthContext";

export function PaymentSimulate() {
  const [sp] = useSearchParams();
  const tx = sp.get("tx_ref");
  const nav = useNavigate();
  const [info, setInfo] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get(`/payments/simulate/${tx}`).then((r) => setInfo(r.data)).catch((e) => toast.error(errMsg(e))); }, [tx]);
  const go = async (outcome) => {
    setBusy(true);
    try {
      await api.post(`/payments/simulate/${tx}`, { outcome });
      nav(`/payment/result?tx_ref=${tx}&status=${outcome === "success" ? "successful" : "failed"}`);
    } catch (e) {
      toast.error(errMsg(e));
      setBusy(false);
    }
  };
  return (
    <div className="grid min-h-[70vh] place-items-center px-4 py-12" data-testid="payment-simulate-page">
      <div className="w-full max-w-md overflow-hidden rounded-lg border bg-white">
        <div className="bg-[#F5A623] px-5 py-3 text-sm font-bold text-ink">Flutterwave · TEST MODE (SIMULATED)</div>
        <div className="space-y-4 p-6">
          {!info ? <Loader2 className="mx-auto h-5 w-5 animate-spin" /> : (
            <>
              <p className="text-sm text-muted-foreground">HostAfricair · {info.kind === "order" ? "New order" : "Invoice payment"}</p>
              <p className="font-display text-4xl font-bold" data-testid="simulate-amount">{money(info.amount, info.currency)}</p>
              <p className="flex items-center gap-2 text-sm"><CreditCard className="h-4 w-4" /> Method: <span className="font-semibold">{info.method.replace("_", " ")}</span></p>
              <p className="rounded bg-amber-50 p-3 text-xs text-amber-800">Flutterwave keys are not connected yet, so this screen stands in for the real checkout.</p>
              <div className="grid grid-cols-2 gap-2">
                <Button variant="outline" onClick={() => go("fail")} disabled={busy} data-testid="simulate-fail-btn">Decline</Button>
                <Button onClick={() => go("success")} disabled={busy} data-testid="pay-now-btn">Pay now</Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export function PaymentResult() {
  const [sp] = useSearchParams();
  const tx = sp.get("tx_ref");
  const tid = sp.get("transaction_id");
  const { user } = useAuth();
  const [state, setState] = useState("checking");

  useEffect(() => {
    let n = 0, stop = false;
    const tick = async () => {
      try {
        const { data } = await api.get("/payments/verify", { params: { tx_ref: tx, transaction_id: tid || undefined } });
        if (data.status === "paid") {
          return setState("paid");
        }
        if (data.status === "failed" || sp.get("status") === "cancelled") return setState("failed");
      } catch {}
      if (++n < 15 && !stop) setTimeout(tick, 2000);
      else setState("pending");
    };
    tick();
    return () => { stop = true; };
  }, [tx]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="grid min-h-[70vh] place-items-center px-4" data-testid="order-confirmation-step">
      <div className="max-w-md text-center">
        {state === "checking" && <><Loader2 className="mx-auto h-10 w-10 animate-spin text-primary" /><p className="mt-4">Confirming your payment…</p></>}
        {state === "paid" && <>
          <CheckCircle2 className="mx-auto h-14 w-14 text-secondary" />
          <h1 className="mt-4 text-3xl font-bold" data-testid="payment-success-title">Payment received</h1>
          <p className="mt-2 text-muted-foreground">Your order is on its way to the network. Track it in your portal.</p>
          <Link to={homeFor(user)}><Button className="mt-6" data-testid="go-to-portal-btn">Go to my portal</Button></Link>
        </>}
        {(state === "failed" || state === "pending") && <>
          <XCircle className="mx-auto h-14 w-14 text-destructive" />
          <h1 className="mt-4 text-3xl font-bold" data-testid="payment-failed-title">{state === "failed" ? "Payment not completed" : "Payment still pending"}</h1>
          <p className="mt-2 text-muted-foreground">{state === "failed" ? "No money was taken. You can try again anytime." : "We'll update your portal as soon as the payment confirms."}</p>
          <Link to="/"><Button variant="outline" className="mt-6">Back to shop</Button></Link>
        </>}
      </div>
    </div>
  );
}
